import React, { CSSProperties } from 'react'
import Button, { ButtonProps } from '../button'
import Box from '../box'
import IconButton from '../icon_button'
import { DeleteForever } from '@mui/icons-material'
import { useShowNotification, PopupNotificationType } from '../../contexts/popup_notification'
import imageCompression from 'browser-image-compression'

export interface AdditionalProps {
  showPreview?: boolean
  compress?: boolean
  previewStyles?: {
    container?: React.CSSProperties
    image?: React.CSSProperties
    imageWidth?: string
    imageHeight?: string
  }
  multiple?: boolean
  maxNumberOfImages?: number
  onUpload: (images: File[]) => void
  images: File[] // TODO: Upload this to work with urls as well
}

export type UploadPhotoButtonProps = ButtonProps & AdditionalProps

const UploadPhotoButton: React.FC<UploadPhotoButtonProps> = ({
  showPreview,
  compress,
  previewStyles: previewStypes,
  multiple,
  onUpload,
  maxNumberOfImages,
  images,
  ...buttonProps
}) => {
  const fileUploaderRef = React.useRef<any>([])
  const showNotification = useShowNotification()

  const handleClickUpload = (): void => {
    fileUploaderRef.current.click()
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    if (e.target.files != null) {
      if (multiple && maxNumberOfImages && e.target.files.length > maxNumberOfImages) {
        showNotification({
          message: `You can only upload a maximum of ${maxNumberOfImages} images`,
          type: PopupNotificationType.ERROR
        })
        return
      }
      let files = Array.from(e.target.files)
      if (compress) {
        const compressedFileOperations = files.map(async (file) => {
          return await imageCompression(file, {
            maxSizeMB: 0.5,
            useWebWorker: true
          })
        })
        files = await Promise.all(compressedFileOperations)
      }
      onUpload(files)
    }
  }

  const previewWrapperStyles: CSSProperties = React.useMemo(() => {
    return {
      width: '100%',
      display: 'flex',
      flexWrap: 'wrap',
      justifyContent: 'left',
      alignItems: 'left',
      gap: '8px',
      ...previewStypes?.container
    }
  }, [previewStypes?.container])

  const previewImageStyles: CSSProperties = React.useMemo(() => {
    return {
      width: previewStypes?.imageWidth ?? '250px',
      height: previewStypes?.imageHeight ?? '250px',
      objectFit: 'cover',
      borderRadius: '8px',
      ...previewStypes?.image
    }
  }, [previewStypes?.image, previewStypes?.imageHeight, previewStypes?.imageWidth])

  const imagePreviews = React.useMemo(() => {
    if (!(showPreview ?? false)) return <></>

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const handleDelete = (index: number): void => {
      const newImages = [...images]
      newImages.splice(index, 1)
      onUpload(newImages)
    }

    return (
      <Box style={previewWrapperStyles}>
        {images.map((image, index) => (
          <Box key={index} position={'relative'}>
            <IconButton color='warning' style={{
              position: 'absolute',
              top: '4px',
              right: '4px',
              zIndex: 100
            }} onClick={() => {
              handleDelete(index)
            }}>
              <DeleteForever />
            </IconButton>
            <img style={previewImageStyles} src={URL.createObjectURL(image)} />
          </Box>
        ))}
      </Box>
    )
  }, [images, onUpload, previewImageStyles, previewWrapperStyles, showPreview])

  const {
    fullWidth = true,
    sx: buttonSX,
    onClick: onButtonClick,
    ...restButtonProps
  } = buttonProps

  return (
    <Box display={'block'} mb={3}>
      {imagePreviews}
      <Button
        sx={{
          marginTop: '12px',
          ...buttonSX
        }}
        fullWidth = {fullWidth}
        onClick={async (e) => {
          e.preventDefault()
          if (onButtonClick != null) {
            await onButtonClick(e)
          }
          handleClickUpload()
        }}
        {...restButtonProps}
        />
      <input
        multiple={multiple}
        ref={fileUploaderRef}
        style={{ display: 'none' }}
        type='file'
        accept='image/*'
        onChange={handleFileUpload}
      />
    </Box>
  )
}

export default UploadPhotoButton
